<template>
  <div>
    <div class="px-6 py-4 analytics-sticky bg-slate-300">
      <div class="flex items-center justify-between">
        <BlTextTitle3 text="Acquisition Overview" />
        <BlInputButton
          type="outlined"
          label="Export to csv"
          :loading="exportLoading"
          @clicked="onExportCSV"
        />
      </div>
      <div class="flex items-center gap-2 py-3">
        <div v-if="enableBranch" class="w-40">
          <BlSelectBox
            type="branch"
            v-model="branchInput"
            :options="branchOptions"
            @change="onChangeBranch($event)"
          />
        </div>
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <BlInputFilter
          class="w-40"
          :input="filterInput"
          @changed="onChangeFilter"
        />
        <BlInputSelect :input="groupByInput" @changed="onChangeGroupBy" />
      </div>
      <div class="flex flex-col gap-2 pt-4">
        <div>
          <BlTextBody2 text="Showing results for " />
          <BlTextSubtitle3 :text="activeDateRange || 'all time'" />
        </div>
        <div
          class="flex gap-5 pb-4 overflow-x-auto overflow-y-hidden scrollbar-lg"
        >
          <div class="flex flex-col flex-grow gap-2">
            <div
              class="flex-grow flex-shrink-0 overflow-x-auto scrollbar-lg"
              :style="{ height: '5.75rem' }"
            >
              <div
                v-if="fetchGraphLoading"
                class="grid h-full rounded-lg place-content-center bg-pure-white"
              >
                <div class="blum-loader-sm"></div>
              </div>
              <div
                v-else-if="!graphData"
                class="grid h-full rounded-lg place-content-center bg-pure-white"
              >
                <BlTextBody2 :text="`Data not found.`" />
              </div>
              <div v-else class="flex gap-2">
                <div
                  v-for="(item, idx) in overviewData"
                  :key="item.label"
                  :title="item.label"
                  class="flex flex-col justify-center flex-shrink-0 gap-1 px-2 transition-all duration-300 border rounded-lg cursor-pointer bg-pure-white hover:bg-slate-200"
                  :class="[
                    item.selected
                      ? 'border-slate-500 shadow'
                      : 'border-transparent'
                  ]"
                  :style="{
                    height: '5rem',
                    width: `calc((100% - ${(overviewData.length - 1) *
                      0.5}rem) / 6)`
                  }"
                  @click="overviewData[idx].selected = !item.selected"
                >
                  <BlTextTitle3
                    v-if="item.is_admitted"
                    :text="item.value"
                    class="self-center"
                    :class="[item.selected ? 'text-lime-600' : 'text-lime-400']"
                  />
                  <BlTextTitle3
                    v-else-if="item.is_pending"
                    :text="item.value"
                    class="self-center text-tulip-600"
                    :class="[item.selected ? '' : 'opacity-50']"
                  />
                  <BlTextTitle3
                    v-else-if="item.is_discontinued"
                    :text="item.value"
                    class="self-center"
                    :class="[item.selected ? 'text-rose-700' : 'text-rose-400']"
                  />
                  <BlTextTitle3
                    v-else
                    :text="item.value"
                    class="self-center"
                    :class="[
                      item.selected ? 'text-slate-900' : 'text-slate-600'
                    ]"
                  />
                  <div class="text-center truncate">
                    <BlTextBody2
                      :text="item.label"
                      class="truncate"
                      :class="{ 'opacity-50': !item.selected }"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!--  -->
            <div
              class="flex-grow flex-shrink-0 rounded-3xl bg-pure-white"
              :style="{ height: '31rem', width: 'auto' }"
            >
              <div
                v-if="fetchGraphLoading"
                class="grid h-full place-content-center"
              >
                <div class="blum-loader"></div>
              </div>
              <div
                v-else-if="!graphData"
                class="grid h-full place-content-center"
              >
                <BlTextBody2 :text="`Data not found.`" />
              </div>
              <div v-else class="flex flex-col gap-3 px-6 py-6">
                <BlTextSubtitle2
                  text="Prospect movement across stages"
                  class="text-slate-700"
                />
                <BarChart
                  :labels="barChartData.labels"
                  :datasets="barChartData.datasets"
                  stacked
                  height="23.5rem"
                  width="calc((100vw - 15rem - 3.5rem) - 3rem - 1.25rem - 13rem)"
                />
                <div
                  class="flex items-center gap-5"
                  :style="{
                    width:
                      'calc((100vw - 15rem - 3.5rem) - 3rem - 1.25rem - 13rem)'
                  }"
                >
                  <div
                    v-for="legend in barChartData.legends.slice(0, 6)"
                    :key="legend.label"
                    :title="legend.label"
                    class="flex items-center gap-1 truncate"
                  >
                    <div
                      class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                      :style="{ backgroundColor: legend.color }"
                    ></div>
                    <div class="truncate">
                      <BlTextBody2 :text="legend.label" />
                    </div>
                  </div>
                  <div
                    class="relative"
                    v-if="barChartData.legends.slice(6).length"
                  >
                    <div
                      class="flex items-center justify-center cursor-pointer"
                      @click="moreLegend = !moreLegend"
                    >
                      <BlBubbleCount
                        :count="`+ ${barChartData.legends.slice(6).length}`"
                        class="h-7 hover:bg-light-purple-100"
                      />
                    </div>
                    <div
                      v-if="moreLegend"
                      class="absolute bottom-0 right-0 z-10 flex flex-col gap-1 p-1 mb-10 truncate border rounded min-w-32 bg-pure-white border-grey-200"
                      :style="{ zIndex: 1 }"
                    >
                      <div
                        v-for="(i, idx) in barChartData.legends
                          .slice(6)
                          .reverse()"
                        :key="idx"
                        class="flex items-center gap-1 px-4 py-2 truncate rounded max-w-64"
                        :title="i.label"
                      >
                        <div
                          class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                          :style="{ backgroundColor: i.color }"
                        ></div>
                        <div class="truncate">
                          <BlTextBody2 :text="i.label" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!--  -->
          <div class="flex flex-col gap-5" :style="{ width: '13rem' }">
            <div
              class="flex-shrink-0 rounded-3xl bg-pure-white"
              :style="{ height: '18rem' }"
            >
              <div
                v-if="fetchGraphLoading"
                class="grid h-full place-content-center"
              >
                <div class="blum-loader"></div>
              </div>
              <div
                v-else-if="!graphData"
                class="grid h-full place-content-center"
              >
                <BlTextBody2 :text="`Data not found.`" />
              </div>
              <div v-else class="flex flex-col gap-3 px-6 py-6">
                <div class="h-10">
                  <BlTextSubtitle2 text="Referral" class="text-slate-700" />
                </div>
                <PieChart
                  :labels="pieChartDataReferral.labels"
                  :colors="pieChartDataReferral.colors"
                  :data="pieChartDataReferral.data"
                  height="6.25rem"
                />
                <div class="flex flex-col gap-3 h-15">
                  <div
                    v-for="legend in pieChartLegendsReferral"
                    :key="legend.label"
                    class="flex items-center justify-between gap-2"
                  >
                    <div
                      class="flex items-center gap-1 truncate"
                      :title="legend.label"
                    >
                      <div
                        class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                        :style="{ backgroundColor: legend.color }"
                      ></div>
                      <BlTextBody2 :text="legend.label" class="truncate" />
                    </div>
                    <div>
                      <BlTextSubtitle3
                        :text="legend.value"
                        class="text-slate-800"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-if="pieChartPageCountReferral > 1"
                  class="flex items-center justify-center"
                >
                  <div
                    v-for="n in pieChartPageCountReferral"
                    :key="n"
                    @click="pieChartPageReferral = n"
                    class="p-1 cursor-pointer"
                  >
                    <div
                      :class="[
                        pieChartPageReferral === n
                          ? 'bg-slate-700'
                          : 'bg-slate-500'
                      ]"
                      class="h-2.5 w-2.5 flex-shrink-0 rounded-full hover:bg-slate-700 transition-all duration-300"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <!--  -->
            <div
              class="flex-shrink-0 rounded-3xl bg-pure-white"
              :style="{ height: '18rem' }"
            >
              <div
                v-if="fetchGraphLoading"
                class="grid h-full place-content-center"
              >
                <div class="blum-loader"></div>
              </div>
              <div
                v-else-if="!graphData"
                class="grid h-full place-content-center"
              >
                <BlTextBody2 :text="`Data not found.`" />
              </div>
              <div v-else class="flex flex-col gap-3 px-6 py-6">
                <div class="h-10">
                  <BlTextSubtitle2
                    text="Discontinued reason"
                    class="text-slate-700"
                  />
                </div>
                <PieChart
                  :labels="pieChartDataReason.labels"
                  :colors="pieChartDataReason.colors"
                  :data="pieChartDataReason.data"
                  height="6.25rem"
                />
                <div class="flex flex-col gap-3 h-15">
                  <div
                    v-for="legend in pieChartLegendsReason"
                    :key="legend.label"
                    class="flex items-center justify-between gap-2"
                  >
                    <div
                      class="flex items-center gap-1 truncate"
                      :title="legend.label"
                    >
                      <div
                        class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                        :style="{ backgroundColor: legend.color }"
                      ></div>
                      <BlTextBody2 :text="legend.label" class="truncate" />
                    </div>
                    <div>
                      <BlTextSubtitle3
                        :text="legend.value"
                        class="text-slate-800"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-if="pieChartPageCountReason > 1"
                  class="flex items-center justify-center"
                >
                  <div
                    v-for="n in pieChartPageCountReason"
                    :key="n"
                    @click="pieChartPageReason = n"
                    class="p-1 cursor-pointer"
                  >
                    <div
                      :class="[
                        pieChartPageReason === n
                          ? 'bg-slate-700'
                          : 'bg-slate-500'
                      ]"
                      class="h-2.5 w-2.5 flex-shrink-0 rounded-full hover:bg-slate-700 transition-all duration-300"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--  -->
    <div>
      <div
        class="z-10 flex items-center justify-between px-6 py-4 analytics-sticky top-18 bg-slate-100"
      >
        <div>
          <BlTextBody2 :text="`Showing all ${tableCount} prospect(s)`" />
          <BlTextBody2
            v-if="currentStageFilter.length"
            :text="
              `based on the filters: date range (${dateFilter ||
                'all time'}) and ${currentStageFilter.length} stage(s).`
            "
          />
          <BlTextBody2
            v-else-if="dateFilter"
            :text="
              `who moved between stages within the date range ${dateFilter}.`
            "
          />
          <BlTextBody2
            v-else
            :text="`who moved between stages for all time.`"
          />
        </div>
        <BlSort
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort"
          dropDirection="left"
        />
      </div>
      <div
        v-if="fetchTableLoading"
        class="grid h-64 analytics-sticky place-content-center"
      >
        <div class="blum-loader"></div>
      </div>
      <TableAcquisitionOverview v-else :tableData="tableData" />
      <div class="px-6 pt-4 analytics-sticky pb-36">
        <BlInputPagination
          v-if="tableCount > 25"
          :page="page"
          :perPage="perPage"
          :total="tableCount"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import moment from "moment";

import BarChart from "./graphs/BarChart.vue";
import PieChart from "./graphs/PieChart.vue";
import TableAcquisitionOverview from "./table/TableAcquisitionOverview.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlSort from "../BlSort.vue";
import BlTooltip from "../BlTooltip.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "AcquisitionOverview",
  components: {
    BarChart,
    PieChart,
    TableAcquisitionOverview,
    BlTextTitle3,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlSort,
    BlTooltip,
    BlBubbleCount,
    BlSelectBox,
    BlInputSearch,
    BlInputFilter,
    BlInputSelect,
    BlInputButton,
    BlInputPagination
  },
  data() {
    const interval = "year";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    const defaultGroupBy = { name: "Month", value: "month" };
    const defaultSort = { primary: "name", secondary: "asc" };

    return {
      defaultDate,
      defaultGroupBy,
      defaultSort,
      /** */
      fetchGraphLoading: false,
      overviewData: [],
      graphData: null,
      /** */
      fetchTableLoading: false,
      tableData: [],
      tableCount: 0,
      /** */
      exportLoading: false,
      /** */
      moreLegend: false,
      branchInput: 0,
      filterInput: {
        model: [defaultDate, []],
        tabs: [
          { name: "Date", kind: "date", all_time: true },
          { name: "Current Stage", kind: "multi-tag", options: [] }
        ]
      },
      groupByInput: {
        model: defaultGroupBy,
        prefix: "Group by",
        combine: true,
        options: [
          { name: "Day", value: "day", disabled: false },
          { name: "Week", value: "week", disabled: false },
          { name: "Month", value: "month" },
          { name: "Year", value: "year" }
        ]
      },
      /** */
      pieChartPageReferral: 1,
      pieChartPageReason: 1,
      pieChartPerPage: 2,
      /** */
      searchInput: { placeholder: "Search", model: "" },
      page: 1,
      perPage: 25,
      sortInput: defaultSort
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "branches", "stages"]),
    enableSalesPipeline() {
      return this.currentCenter.enable_sales_pipeline;
    },
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    searchParams() {
      return this.$route.query || {};
    },
    paramsTab() {
      return this.$route.params.tab;
    },
    activeDateRange() {
      const start = this.searchParams["start_date"];
      const end = this.searchParams["end_date"];
      if (start && end) {
        const s = moment(start).format("DD/MM/YYYY");
        const e = moment(end).format("DD/MM/YYYY");
        const diff = moment(end).diff(moment(start), "days") + 1;
        return `${s} - ${e}, ${diff} day(s)`;
      }
      return "";
    },
    dateFilter() {
      const start = this.searchParams["start_date"];
      const end = this.searchParams["end_date"];
      if (start && end) {
        const s = moment(start).format("DD/MM/YYYY");
        const e = moment(end).format("DD/MM/YYYY");
        return `${s} - ${e}`;
      }
      return "";
    },
    currentStageFilter() {
      return this.filterInput.model[1];
    },
    branchOptions() {
      const allBranch = {
        value: 0,
        label: "All branches",
        class: "-mx-2"
      };
      const arr = this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
      return [allBranch, ...arr];
    },
    sortOptions() {
      const primary = [
        { value: "id", label: "ID" },
        { value: "name", label: "Prospect name" }
      ];
      const stages = this.stages.map(i => {
        const camelCase = i.name.toLowerCase().replaceAll(" ", "_");
        const value = `date_moved_to_${camelCase}`;
        const label = `Moved to ${i.name}`;
        return { value, label };
      });
      const secondary = [
        { value: "asc", label: "Ascending" },
        { value: "desc", label: "Descending" }
      ];
      return { primary: [...primary, ...stages], secondary };
    },
    getFilterParams() {
      // return start_date, end_date, branch_ids
      let params = ``;
      const dates = this.filterInput.model.at(0) || {
        start_date: "",
        end_date: ""
      };

      if (dates.start_date) {
        params += `&start_date=${dates.start_date}`;
      }
      if (dates.end_date) {
        params += `&end_date=${dates.end_date}`;
      }
      if (this.branchInput) {
        params += `&branch_ids=${this.branchInput}`;
      }

      if (params.length > 1) params = "?" + params.slice(1);
      else params = "?all=true";

      return params;
    },
    /*
    "data": [
      {
        "date": "2024-01-01",
        "inquiry": 11,
        "consultation": 8,
        "assessment": 0,
        "pending abc": 3,
        "admitted": 9,
        "discontinued": 5
      }
    ]
    */
    barChartData() {
      const n = { labels: [], datasets: [], legends: [], empty: true };
      if (!this.graphData) return n;
      const raw = this.graphData.data;
      if (!raw) return n;
      const keys = this.graphData.overview.map(i => i.stage_name).reverse();
      // discontinued, pending, admitted, stage_name, dst
      const colors = [
        "#F63D68", // rose-7
        "#E8B100", // tulip-7
        "#B9D843", // lime-5
        "#0B4F6F", // cornflower-10
        "#066086",
        "#0172A3",
        "#0095D1",
        "#0CB2EB",
        "#36C8FA",
        "#7CDAFD",
        "#B9EAFE",
        "#E0F4FE",
        "#F0FAFF" // cornflower-1
      ];
      function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      }
      const texts = keys.map(i => capitalizeFirstLetter(i));

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      const legends = keys.map((key, idx) => ({
        label: texts[idx],
        color: colors[idx]
      }));
      let empty = true;

      const formats = {
        day: "DD/MM/YY",
        week: "DD/MM",
        month: "MMM YYYY",
        year: "YYYY"
      };
      const groupBy = this.groupByInput.model.value;

      raw.forEach(i => {
        let l = moment(i.date)
          .format(formats[groupBy])
          .toUpperCase();
        if (groupBy === "week") {
          l += `-${moment(i.date)
            .add(6, "days")
            .format(formats[groupBy])}`;
        }
        labels.push(l);
        keys.forEach((key, idx) => {
          let value = 0;
          const overview = this.overviewData.find(
            i => i.label.toLowerCase() === key.toLowerCase()
          );
          if (overview && overview.selected) value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return {
        labels,
        datasets: datasets.reverse(),
        legends: legends.reverse(),
        empty
      };
    },
    /*
    "referral": {
      "doctor_referral": 1,
      "friend_or_family": 3,
      "outside_provider": 2,
      "school_or_daycare": 1,
      "social_media": 1
    },
    */
    pieChartDataReferral() {
      const n = { labels: [], colors: [], data: [], legends: [] };
      if (!this.graphData) return n;
      const raw = this.graphData.referral;
      if (!raw) return n;
      const keys = [
        "friend_or_family",
        "outside_provider",
        "school_or_daycare",
        "doctor",
        "walk_in",
        "website",
        "social_media",
        "community_event",
        "other"
      ];
      const labels = [
        "Friend/family",
        "Outside provider (OT, PT, ST)",
        "School/daycare",
        "Doctor",
        "Walk-in",
        "Website",
        "Social media",
        "Community event",
        "Other"
      ];
      const colors = [
        "#10B981",
        "#6EE7B7",
        "#D1FAE5",
        "#047857",
        "#064E3B",
        "#14B8A6",
        "#5EEAD4",
        "#CCFBF1",
        "#EAECF0"
      ];
      const data = keys.map(key => raw[key] || 0);
      const legends = keys
        .map((key, idx) => ({
          label: labels[idx],
          color: colors[idx],
          value: data[idx]
        }))
        .sort((a, b) => b.value - a.value);
      return { labels, colors, data, legends };
    },
    pieChartLegendsReferral() {
      const legends = this.pieChartDataReferral.legends;
      const start = (this.pieChartPageReferral - 1) * this.pieChartPerPage;
      const end = start + this.pieChartPerPage;
      return legends.slice(start, end);
    },
    pieChartPageCountReferral() {
      const legends = this.pieChartDataReferral.legends;
      const dataLength = legends.length;
      return Math.ceil(dataLength / this.pieChartPerPage) || 1;
    },
    /*
    "discontinued": {
      "fees_too_high": 3,
      "hours_dont_fit_schedule": 1,
      "other": 1
    },
    */
    pieChartDataReason() {
      const n = { labels: [], colors: [], data: [], legends: [] };
      if (!this.graphData) return n;
      const raw = this.graphData.discontinued;
      if (!raw) return n;
      const keys = [
        "unable_to_contact_family",
        "distance_too_far",
        "hours_dont_fit_schedule",
        "seeking_service_not_provided",
        "resides_in_a_different_state",
        "too_old",
        "fees_too_high",
        "no_show_response",
        "do_not_need_therapy",
        "ongoing_client",
        "other"
      ];
      const labels = [
        "Unable to contact family",
        "Distance too far",
        "Hours don't fit schedule",
        "Seeking services not provided",
        "Resides in different state",
        "Too old",
        "Fees too high",
        "No show/response",
        "Do not need therapy",
        "Ongoing client",
        "Other"
      ];
      const colors = [
        "#F63D68",
        "#FEA3B4",
        "#FFE4E8",
        "#FFF5F6",
        "#58346F",
        "#8049A7",
        "#AD7BD3",
        "#DAC5ED",
        "#E7DAF4",
        "#FD6F8E",
        "#F4EDFA"
      ];
      const data = keys.map(key => raw[key] || 0);
      const legends = keys
        .map((key, idx) => ({
          label: labels[idx],
          color: colors[idx],
          value: data[idx]
        }))
        .sort((a, b) => b.value - a.value);
      return { labels, colors, data, legends };
    },
    pieChartLegendsReason() {
      const legends = this.pieChartDataReason.legends;
      const start = (this.pieChartPageReason - 1) * this.pieChartPerPage;
      const end = start + this.pieChartPerPage;
      return legends.slice(start, end);
    },
    pieChartPageCountReason() {
      const legends = this.pieChartDataReason.legends;
      const dataLength = legends.length;
      return Math.ceil(dataLength / this.pieChartPerPage) || 1;
    }
  },
  watch: {
    moreLegend(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerLegend);
        }, 10);
      }
    },
    stages: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val.length) {
          this.filterInput.tabs[1].options = val.map(i => {
            const res = {
              name: i.name,
              value: i.id,
              color: "#0172A3", // cornflower-8
              backgroundColor: "#E0F4FE" // cornflower-2
            };
            if (i.is_admitted) {
              res.color = "#789719"; // lime-7
              res.backgroundColor = "#F2F8CF"; //
            }
            if (i.is_pending) {
              res.color = "#D18D00"; // tulip-7
              res.backgroundColor = "#FFFDE7"; // tulip-1
            }
            if (i.is_discontinued) {
              res.color = "#F63D68"; // rose-7
              res.backgroundColor = "#FFF1F3"; // rose-2
            }
            return res;
          });
        }
      }
    }
  },
  mounted() {
    if (!this.enableSalesPipeline) {
      return this.$router.push({ name: "Analytics" });
    }
    let filter = [this.defaultDate, []];
    const searchParams = this.searchParams;

    if (searchParams["start_date"]) {
      filter[0].start_date = searchParams["start_date"];
    }
    if (searchParams["end_date"]) {
      filter[0].end_date = searchParams["end_date"];
    }
    if (searchParams["stage_ids"]) {
      const arr = searchParams["stage_ids"].split(",");
      filter[1] = arr.map(a => ({ value: Number(a) }));
    }
    this.filterInput.model = filter;
    if (searchParams["branch_ids"]) {
      this.branchInput = Number(searchParams["branch_ids"]);
    }
    if (searchParams["group_by"]) {
      const n = this.groupByInput.options.find(
        i => i.value === searchParams["group_by"]
      );
      if (n) this.groupByInput.model = n;
    }
    /** */
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["page"];
    }
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["sort_by"]) {
      const n = searchParams["sort_by"] || "";
      if (n.includes("_")) {
        const arr = n.split("_");
        const secondary = arr.pop();
        const primary = arr.join("_");
        this.sortInput = { primary, secondary };
      }
    }

    this.fetchGraph();
    this.fetchTable();
  },
  methods: {
    ...mapActions(["actionGet", "actionDownload"]),
    syncPath() {
      let params = this.getFilterParams;
      params += `&group_by=${this.groupByInput.model.value}`;
      /** */
      const stages = this.filterInput.model.at(1) || [];
      if (stages.length > 0) {
        params += `&stage_ids=${stages.map(a => a.value).join(",")}`;
      }
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      params += `&page=${this.page}&per_page=${this.perPage}`;
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      // sync url path for page
      const incomingPath = "/analytics/acquisition-overview" + params;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    fetchGraph() {
      /*
        start_date, end_date, branch_ids,
        --
        group_by: day | week | month | year
      */
      const start = this.filterInput.model[0].start_date;
      const end = this.filterInput.model[0].end_date;
      this.groupByInput.options[0].disabled = false;
      this.groupByInput.options[1].disabled = false;
      if (start && end) {
        const diff = moment(end).diff(moment(start), "days") + 1;
        if (diff >= 365) {
          this.groupByInput.options[0].disabled = true;
          this.groupByInput.options[1].disabled = true;
          const groupByDayOrWeek =
            this.groupByInput.model.value === "day" ||
            this.groupByInput.model.value === "week";
          if (groupByDayOrWeek) {
            this.groupByInput.model = { name: "Month", value: "month" };
          }
        }
      }

      let params = this.getFilterParams;
      params += `&group_by=${this.groupByInput.model.value}`;
      const data = {
        url: `/api/v1/analytics/acquisition_overview_graph${params}`
      };
      this.fetchGraphLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchGraphLoading = false;
        if (!success) return;
        this.graphData = data;
        this.generateOverview(data.overview || []);
      });
      this.syncPath();
    },
    fetchTable() {
      /*
        start_date, end_date, branch_ids,
        --
        stage_ids,
        page, per_page,
        query,
        sort_by:
          | name_asc | name_desc
          | date_moved_to_stage_name_asc | date_moved_to_stage_name_desc (generated)
      */
      let params = this.getFilterParams;
      const stages = this.filterInput.model.at(1) || [];
      if (stages.length > 0) {
        params += `&stage_ids=${stages.map(a => a.value).join(",")}`;
      }
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      params += `&page=${this.page}&per_page=${this.perPage}`;
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      const data = {
        url: `/api/v1/analytics/acquisition_overview_table${params}`
      };
      this.fetchTableLoading = true;
      this.tableData = [];
      this.tableCount = 0;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchTableLoading = false;
        if (!success) return;
        this.tableData = data.prospects;
        this.tableCount = data.total_count;
      });
      this.syncPath();
    },
    onExportCSV() {
      /*
        start_date, end_date, branch_ids,
        --
        stage_ids,
        sort_by:
          | name_asc | name_desc
          | date_moved_to_stage_name_asc | date_moved_to_stage_name_desc (generated)
      */
      let params = this.getFilterParams;
      const stages = this.filterInput.model.at(1) || [];
      if (stages.length > 0) {
        params += `&stage_ids=${stages.map(a => a.value).join(",")}`;
      }
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      const data = {
        url: `/api/v1/analytics/acquisition_overview_csv${params}`,
        title: "",
        message: ""
      };
      this.exportLoading = true;
      this.actionDownload(data).then(({ success }) => {
        this.exportLoading = false;
        // if (!success) return;
      });
    },
    generateOverview(arr) {
      /*
      "overview": [
        { "stage_name": "Inquiry", "value": 11 },
        { "stage_name": "Consultation", "value": 8 },
        { "stage_name": "Assessment", "value": 0 },
        { "stage_name": "Admitted", "value": 9 },
        { "stage_name": "Pending", "value": 3 },
        { "stage_name": "Discontinued", "value": 5 }
      ]
      */
      this.overviewData = [];
      if (!arr) return [];
      const filtered = [];
      this.currentStageFilter.forEach(i => {
        const found = this.stages.find(stage => stage.id === i.value);
        if (found) filtered.push(found);
      });
      this.overviewData = arr.map(i => {
        let found;
        if (filtered.length) {
          found = filtered.find(filter => filter.name === i.stage_name);
        }
        const selected = !filtered.length ? true : found !== undefined;
        return {
          label: i.stage_name,
          value: i.value,
          is_admitted: i.stage_name === "Admitted",
          is_pending: i.stage_name === "Pending",
          is_discontinued: i.stage_name === "Discontinued",
          selected
        };
      });
    },
    /** */
    onChangeBranch(val) {
      this.branchInput = val;
      this.fetchGraph();
      this.fetchTable();
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchTable();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchGraph();
      this.fetchTable();
    },
    onChangeGroupBy(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchGraph();
      this.fetchTable();
    },
    closeHandlerLegend() {
      window.removeEventListener("click", this.closeHandlerLegend);
      this.moreLegend = false;
    },
    /** */
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchTable();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchTable();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchTable();
    }
  }
};
</script>

<style scoped lang="scss">
.analytics-sticky {
  width: calc(100vw - 15rem - 0.5rem);
  position: sticky;
  left: 15rem;
}
</style>



// WEBPACK FOOTER //
// src/components/analytics/AcquisitionOverview.vue